/**
 * DataHealthPanel — orquestador del panel "Data Health" (renombre de "Data Issues").
 *
 * Reemplaza el entry "Data Issues" del sidebar admin con un panel que tiene 3 sub-tabs:
 *   - Reportes : panel existente DataIssuesPanel (sin redesign — decisión #5)
 *   - Ocultos  : list view de productos con visibility=0
 *   - Dudosos  : list view de productos visibles pero sospechosos
 *
 * Spec UX: docs/architecture/DATA_HEALTH_PANEL_SPEC.md
 * Backend: docs/architecture/VISIBILITY_AND_HISTORY_SPEC.md (sub-agente paralelo)
 *
 * Decisiones aprobadas:
 *  1. Toggle "Incluir ocultos" sólo superadmin (default OFF) — aplica a tab Ocultos.
 *  4. Bulk Re-catalogar real con modal de costo (sólo en tab Dudosos por ahora).
 *  5. Tab Reportes cosmético, sin redesign de DataIssuesPanel.
 *  6. Color tab activa = azul (NO rosa, NO purple).
 */
import React, { useEffect, useState } from 'react';
import { HeartPulse, Flag, EyeOff, AlertTriangle, Check, ListChecks } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ReportsTab from './dataHealth/ReportsTab';
import HiddenItemsTab from './dataHealth/HiddenItemsTab';
import DubiousItemsTab from './dataHealth/DubiousItemsTab';
import RevisionQueue from './dataHealth/RevisionQueue';

interface Props {
  clientId: string;
  clientName?: string;
}

type SubTab = 'reportes' | 'ocultos' | 'dudosos' | 'revision';

const SUBTABS: { id: SubTab; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  {
    id: 'reportes',
    label: 'Data Issues',
    icon: Flag,
    description:
      'Reportes y propuestas de edición enviados por usuarios. Triage + aprobación de cambios al catálogo.',
  },
  {
    id: 'ocultos',
    label: 'Ocultos',
    icon: EyeOff,
    description:
      'Productos hidden — no aparecen en publicaciones ni stats. Acá los recuperás o ajustás el motivo.',
  },
  {
    id: 'dudosos',
    label: 'Dudosos',
    icon: AlertTriangle,
    description:
      'Productos catalogados con baja confianza, subcategoría null, marca no whitelist u otros flags. Decidí: aprobar, ocultar o re-catalogar.',
  },
  {
    id: 'revision',
    label: 'Cola de revisión',
    icon: ListChecks,
    description:
      'Curación profunda por escenario (rechazados, low conf, brand ghost…). Aprobar/revivir items + capturar feedback para retroalimentar el prompt. SOLO superadmin.',
  },
];

const DataHealthPanel: React.FC<Props> = ({ clientId, clientName }) => {
  const [subtab, setSubtab] = useState<SubTab>('reportes');
  const { userData } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';

  const activeMeta = SUBTABS.find((t) => t.id === subtab)!;

  // Toast simple (mismo patrón que CatalogadorPanel línea 224-230).
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-blue-600" />
          <h3 className="text-base font-semibold text-gray-900">Data Health</h3>
          {clientName && <span className="text-xs text-gray-400">— {clientName}</span>}
        </div>
        {toast && (
          <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1">
            <Check className="w-3 h-3" />
            {toast}
          </div>
        )}
      </div>

      {/* Subtabs — activa azul (decisión #6). 'revision' solo superadmin. */}
      <div className="flex border-b border-gray-200 px-2">
        {SUBTABS.filter((t) => t.id !== 'revision' || isSuperadmin).map((t) => {
          const Icon = t.icon;
          const active = subtab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSubtab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? 'border-blue-700 text-blue-700 bg-blue-50'
                  : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Descripción tab activa */}
      <div className="px-4 pt-3">
        <div className="bg-indigo-50 border-l-2 border-indigo-300 rounded px-3 py-2 text-xs text-gray-600">
          {activeMeta.description}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {subtab === 'reportes' && <ReportsTab clientId={clientId} clientName={clientName} />}
        {subtab === 'ocultos' && (
          <HiddenItemsTab
            clientId={clientId}
            isSuperadmin={isSuperadmin}
            onToast={setToast}
          />
        )}
        {subtab === 'dudosos' && (
          <DubiousItemsTab
            clientId={clientId}
            isSuperadmin={isSuperadmin}
            onToast={setToast}
          />
        )}
        {subtab === 'revision' && isSuperadmin && (
          <RevisionQueue clientId={clientId} onToast={setToast} />
        )}
        {subtab === 'revision' && !isSuperadmin && (
          <div className="text-xs text-gray-500 italic px-3 py-6 text-center">
            Acceso restringido — solo superadmin.
          </div>
        )}
      </div>
    </div>
  );
};

export default DataHealthPanel;
